import React from 'react';
import dayjs from 'dayjs';
import parse, { attributesToProps, domToReact } from 'html-react-parser';

import { socialCardKeys, SOCIAL_CARD_TYPE } from '@/src/constants/socialcard.constant';
import ClassNames from '@/src/components/componentHelper/ClassNames';
import ReadMoreReadLess from '@/src/components/componentHelper/ReadMoreReadLess';
import RenderGraphicIcon from '../graphic-card/RenderGraphicIcon';

/**
 * Shared building blocks for the studio card set (widget types 10-19).
 *
 * Ported from famewall-React/src/components/cards/studio-cards/StudioCardKit
 * with two differences, both forced by the embed environment:
 *   - the theme arrives as a `t` prop instead of a context hook
 *   - dates use dayjs (the embeds don't ship moment)
 * The dashboard-only pieces (DashboardTools, PaidBadge) are deliberately not
 * ported - an embed has no editing UI and must never show plan warnings.
 */

/* ---------------------------------------------------------------- wrapper */

/**
 * Positioning only - NO vertical padding.
 *
 * Muuri adds no margin of its own, so any py-* here becomes the gap between
 * stacked cards, doubled. The default types (Simplistic, Graphic, Grey) carry
 * none and sit tight; py-3 here put every studio card 24px further apart than
 * those. A card that genuinely needs room asks via `style` - Pinboard, which is
 * rotated and has tape overhanging its top edge.
 */

export const StudioWrap = ({ children, style }) => (
  <div className="mx-auto" style={{ maxWidth: '40rem', minWidth: '100%', ...(style || {}) }}>
    {children}
  </div>
);

/* ------------------------------------------------------------- body text */

export const RichText = ({ t, cardItem, style, className, length = 300 }) => {
  const content = cardItem && cardItem[socialCardKeys.messageContent];
  if (!content || content === '') return null;

  const options = {
    replace: (domNode) => {
      if (!domNode.attribs) return undefined;
      const attribProps = attributesToProps(domNode.attribs);
      if (domNode.name === 'a') {
        return (
          <a {...attribProps} style={{ color: t.link }}>
            {domToReact(domNode.children, options)}
          </a>
        );
      }
      if (domNode.name === 'b') {
        const bStyle = { backgroundColor: t.highlight };
        if (t.dark) bStyle.color = '#3f3a3a';
        return <b style={bStyle}>{domToReact(domNode.children, options)}</b>;
      }
      return undefined;
    },
  };

  return (
    <p
      className={ClassNames('tweet-content', t.fontClass, className || '')}
      style={{ color: t.ink, margin: 0, overflowWrap: 'anywhere', ...(style || {}) }}
    >
      {t.shortenText ? (
        <ReadMoreReadLess
          elementId={cardItem[socialCardKeys.uuid]}
          refresh={() => t.refresh && t.refresh()}
          text={content}
          parse={parse}
          options={options}
          length={length}
          readMore={'Read More'}
          readLess={'Read Less'}
        />
      ) : (
        parse(content, options)
      )}
    </p>
  );
};

/* ----------------------------------------------------------------- stars */

const FullStar = ({ size }) => (
  <svg viewBox="0 0 576 512" style={{ width: size, height: size }} aria-hidden="true">
    <path
      fill="currentColor"
      d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"
    />
  </svg>
);

const HalfStar = ({ size }) => (
  <svg viewBox="0 0 536 512" style={{ width: size, height: size }} aria-hidden="true">
    <path
      fill="currentColor"
      d="M508.55 171.51L362.18 150.2 296.77 17.81C290.89 5.98 279.42 0 267.95 0c-11.4 0-22.79 5.9-28.69 17.81l-65.43 132.38-146.38 21.29c-26.25 3.8-36.77 36.09-17.74 54.59l105.89 103-25.06 145.48C86.98 495.33 103.57 512 122.15 512c4.93 0 10-1.17 14.87-3.75l130.95-68.68 130.94 68.7c4.86 2.55 9.92 3.71 14.83 3.71 18.6 0 35.22-16.61 31.66-37.4l-25.03-145.49 105.91-102.98c19.04-18.5 8.52-50.8-17.73-54.6zm-121.74 123.2l-18.12 17.62 4.28 24.88 19.52 113.45-102.13-53.59-22.38-11.74.03-317.19 51.03 103.29 11.18 22.63 25.01 3.64 114.23 16.63-82.65 80.38z"
    />
  </svg>
);

export const Stars = ({ t, cardItem, size = 14, color, className, style }) => {
  const rating = cardItem && cardItem[socialCardKeys.starRating];
  if (!rating || rating <= 0) return null;

  const full = Math.min(5, Math.floor(rating));
  const half = rating % 1 === 0.5 && full < 5;

  return (
    <span
      className={ClassNames('inline-flex items-center', className || '')}
      style={{ color: color || t.accent, gap: '2px', ...(style || {}) }}
    >
      {[...Array(full)].map((_, i) => (
        <FullStar key={`star-${i}`} size={size} />
      ))}
      {half ? <HalfStar size={size} /> : null}
    </span>
  );
};

/** Numeric score for cards that lead with the number instead of the row. */
export const scoreValue = (cardItem) => {
  const rating = cardItem && cardItem[socialCardKeys.starRating];
  if (!rating || rating <= 0) return null;
  const n = Number(rating);
  return n % 1 === 0 ? `${n}.0` : `${n}`;
};

/* ---------------------------------------------------------------- avatar */

export const Avatar = ({ cardItem, size = 40, ring, ringWidth = 3, radius = '100px', hidePlaceholder = false, style }) => {
  const name = (cardItem && cardItem[socialCardKeys.authorName]) || '';

  /* A picture testimonial already leads with an image; a generated initials
     placeholder next to it is just noise, so callers can suppress it. A real
     author photo still shows. */
  if (hidePlaceholder && !(cardItem && cardItem[socialCardKeys.authorImage])) return null;

  const src =
    (cardItem && cardItem[socialCardKeys.authorImage]) ||
    `https://ui-avatars.com/api/?name=${encodeURI(name)}&background=d0d0e1&color=47476b&size=${Math.max(
      48,
      size * 2
    )}`;

  return (
    <span
      className="inline-flex items-center justify-center flex-shrink-0 overflow-hidden"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        ...(ring ? { boxShadow: `0 0 0 ${ringWidth}px ${ring}` } : {}),
        ...(style || {}),
      }}
    >
      {/* plain img, not next/image: these are arbitrary customer-hosted URLs
          and the embed has no loader configured for them */}
      <img src={src} alt={name} className="w-full h-full object-cover" style={{ borderRadius: radius }} />
    </span>
  );
};

/* -------------------------------------------------------------- identity */

/** The secondary line under a name: role, then tagline, then @handle. */
export const secondaryLine = (cardItem) => {
  if (!cardItem) return null;
  if (cardItem[socialCardKeys.workTitle] && cardItem[socialCardKeys.workTitle] !== '')
    return cardItem[socialCardKeys.workTitle];
  if (cardItem[socialCardKeys.authorTagline] && cardItem[socialCardKeys.authorTagline] !== '')
    return cardItem[socialCardKeys.authorTagline];
  if (cardItem[socialCardKeys.authorUsername] && cardItem[socialCardKeys.authorUsername] !== '')
    return `@${cardItem[socialCardKeys.authorUsername]}`;
  return null;
};

export const Identity = ({
  t,
  cardItem,
  align = 'left',
  nameSize = 14,
  subSize = 12.5,
  nameColor,
  subColor,
  nameWeight = 600,
  truncate = true,
  style,
}) => {
  const link = cardItem && cardItem[socialCardKeys.messageLink];
  const sub = secondaryLine(cardItem);

  const clip = truncate
    ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }
    : {};

  return (
    <div
      className={ClassNames('flex flex-col', t.fontClass)}
      style={{ minWidth: 0, textAlign: align, ...(style || {}) }}
    >
      <a
        href={link || undefined}
        target={link ? '_blank' : '_self'}
        rel="noreferrer noopener"
        style={{
          color: nameColor || t.ink,
          fontWeight: nameWeight,
          fontSize: nameSize,
          lineHeight: 1.25,
          textDecoration: 'none',
          ...clip,
        }}
      >
        {cardItem && cardItem[socialCardKeys.authorName]}
      </a>
      {sub ? (
        <span
          style={{
            color: subColor || t.muted,
            fontSize: subSize,
            lineHeight: 1.35,
            marginTop: 2,
            ...clip,
          }}
        >
          {sub}
        </span>
      ) : null}
    </div>
  );
};

/* ----------------------------------------------------------- source logo */

const NON_PLATFORM_TYPES = [
  SOCIAL_CARD_TYPE.TEXT,
  SOCIAL_CARD_TYPE.VIDEO,
  SOCIAL_CARD_TYPE.AUDIO,
  SOCIAL_CARD_TYPE.PICTURE,
  SOCIAL_CARD_TYPE.STORIES,
];

/** True when this card came from a platform we can badge with a logo. */
export const hasSourceLogo = (cardItem) => {
  if (!cardItem) return false;
  const type = cardItem[socialCardKeys.socialCardType];
  if (type === undefined || type === null) return false;
  return NON_PLATFORM_TYPES.indexOf(type) === -1;
};

/** Platform logo for imported / third-party reviews. Nothing for own testimonials. */
export const SourceLogo = ({ cardItem }) => {
  if (!hasSourceLogo(cardItem)) return null;
  return (
    RenderGraphicIcon(cardItem[socialCardKeys.socialCardType], cardItem[socialCardKeys.messageLink]) || null
  );
};

/* ------------------------------------------------------------------ date */

export const DateText = ({ t, cardItem, color, size = 12, style }) => {
  if (t.hideDate || !cardItem || !cardItem[socialCardKeys.messageTime]) return null;
  return (
    <span className={t.fontClass} style={{ color: color || t.muted, fontSize: size, ...(style || {}) }}>
      {dayjs(cardItem[socialCardKeys.messageTime]).locale('en').format('DD MMM YYYY')}
    </span>
  );
};

/* ------------------------------------------------------------ engagement */

export const Engagement = ({ t, cardItem, color, size = 12 }) => {
  if (t.engagementDisabled || !cardItem) return null;

  const likes = cardItem[socialCardKeys.likesCount] || cardItem[socialCardKeys.upvotesCount];
  const comments = cardItem[socialCardKeys.commentsCount];
  if (!likes && !comments) return null;

  return (
    <span className="inline-flex items-center" style={{ color: color || t.muted, fontSize: size, gap: 10 }}>
      {likes ? (
        <span className="inline-flex items-center" style={{ gap: 4 }}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            style={{ width: 13, height: 13 }}
          >
            <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 000-7.8z" />
          </svg>
          {likes}
        </span>
      ) : null}
      {comments ? (
        <span className="inline-flex items-center" style={{ gap: 4 }}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            style={{ width: 13, height: 13 }}
          >
            <path d="M21 11.5a8.4 8.4 0 01-9 8.4 8.9 8.9 0 01-4-.9L3 21l2-3.9A8.4 8.4 0 1121 11.5z" />
          </svg>
          {comments}
        </span>
      ) : null}
    </span>
  );
};

/* ----------------------------------------------------------- media image */

export const MediaImage = ({ t, cardItem, radius = 10, style }) => {
  const src = cardItem && cardItem[socialCardKeys.messageMediaImage];
  if (!src) return null;
  return (
    <div className="w-full" style={{ position: 'relative', ...(style || {}) }}>
      <img
        src={src}
        alt="Attachment"
        onLoad={() => t.refresh && t.refresh()}
        style={{ width: '100%', borderRadius: radius, objectFit: 'contain', display: 'block' }}
      />
    </div>
  );
};
